import { Injectable } from '@angular/core';
import { Amount, ExchangeRates } from '@qvs/core';
import { Store } from '@ngrx/store';
import { AmountHelper } from '~/shared/helpers/amount-helper';
import { selectCurrencies } from '~/modules/currencies/slice';
import { selectExchangeRates } from '~/modules/exchangeRates/slice';

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  currencyDecimalMap: { [currencyId: string]: number } = {};
  exchangeRates: ExchangeRates;

  constructor(private readonly store: Store) {
    this.store.select(selectCurrencies).subscribe((currencies) => {
      currencies.forEach((currency) => (this.currencyDecimalMap[currency.currency] = currency.decimals));
    });
    this.store.select(selectExchangeRates).subscribe((exchangeRates) => {
      this.exchangeRates = exchangeRates;
    });
  }

  public static convertCurrency(amount: Amount, targetCurrencyId: string, exchangeRates: ExchangeRates): Amount {
    if (!exchangeRates) {
      return amount;
    }

    if (exchangeRates[amount.currency] && exchangeRates[amount.currency][targetCurrencyId]) {
      return {
        currency: targetCurrencyId,
        value: amount.value / exchangeRates[amount.currency][targetCurrencyId]
      };
    }

    if (!exchangeRates[amount.currency] && Object.keys(exchangeRates).length > 0) {
      const refCurrencyId = Object.keys(exchangeRates)[0];
      if (exchangeRates[refCurrencyId][amount.currency]) {
        const refAmount = amount.value * exchangeRates[refCurrencyId][amount.currency];
        if (exchangeRates[refCurrencyId][targetCurrencyId]) {
          return {
            currency: targetCurrencyId,
            value: refAmount / exchangeRates[refCurrencyId][targetCurrencyId]
          };
        }
      }
    }

    return amount;
  }

  toConversionString(source: Amount, targetCurrencyId: string): string {
    if (!source?.value || isNaN(source.value) || source.currency === targetCurrencyId || !targetCurrencyId || !source.currency) {
      return '';
    }
    const targetAmount = this.conversion(source, targetCurrencyId);
    if (targetAmount.currency === source.currency) {
      return '';
    }
    return `Est. c/v ${AmountHelper.toString(
      targetAmount,
      true,
      undefined,
      undefined,
      undefined,
      this.getCurrencyDecimals(targetCurrencyId)
    )}`;
  }

  conversion(source: Amount, targetCurrencyId: string): Amount {
    if (source.currency === targetCurrencyId) {
      return source;
    }
    return CurrencyService.convertCurrency(source, targetCurrencyId, this.exchangeRates);
  }

  isNegativeAfterMinusOperation(operand1: Amount, minusOperand?: Amount): boolean {
    return operand1.value - this.conversion(minusOperand, operand1.currency).value < 0;
  }

  getCurrencyDecimals(currencyId: string): number {
    return this.currencyDecimalMap[currencyId] === undefined ? 2 : this.currencyDecimalMap[currencyId];
  }
}
